import useFetchWithMsal from '../../hooks/useFetchWithMsal';
import React, { useState, useEffect, useCallback } from 'react';
import { protectedResources, scopes } from "../../authConfig";
import Select from 'react-select';

function ForeignTableSelect({ type, displayName, value, onChange, name, relatedInfo, parentValue }) {
    const { execute } = useFetchWithMsal({
        scopes: scopes.callapi
    });

    const [selectData, setSelectData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const typeName = type + "Name";
    
    // フィルタリングロジックを関数化し、useCallbackでメモ化
    const filterDataByParent = useCallback((data, pValue, rInfo) => {
        if (!pValue || pValue === "" || !rInfo || !rInfo.foreignKeyField) {
            console.log(`ForeignTableSelect(${type}): No valid filter parameters, showing all options`);
            setFilteredData(data);
            return;
        }
        
        console.log(`ForeignTableSelect(${type}): Filtering with parentValue=${pValue}, foreignKeyField=${rInfo.foreignKeyField}`);
        
        // 文字列または数値の比較に対応
        const filtered = data.filter(item => {
            const itemValue = item[rInfo.foreignKeyField];
            
            // 両方の値が存在する場合に比較
            if (itemValue !== undefined && pValue !== undefined) {
                // 文字列として比較してから、可能であれば数値として比較
                const stringMatch = String(itemValue) === String(pValue);
                
                // 数値型に変換可能な場合は数値として比較
                const parentValueNum = Number(pValue);
                const itemValueNum = Number(itemValue);
                const numberMatch = !isNaN(parentValueNum) && !isNaN(itemValueNum) && 
                                   itemValueNum === parentValueNum;
                
                const matches = stringMatch || numberMatch;
                console.log(`Item ${item.id} ${typeName}: ${item[typeName]}, ${rInfo.foreignKeyField}=${itemValue}, matches=${matches}`);
                return matches;
            }
            return false;
        });
        
        console.log(`ForeignTableSelect(${type}): Filtered data (${filtered.length} items):`, filtered);
        setFilteredData(filtered);
        
        // 現在の値が新しいフィルタリング結果に存在しない場合、値をリセット
        if (value && filtered.length > 0) {
            const valueExists = filtered.some(item => {
                // 文字列または数値として比較
                return String(item.id) === String(value) || 
                      (!isNaN(Number(item.id)) && !isNaN(Number(value)) && Number(item.id) === Number(value));
            });
            
            if (!valueExists) {
                console.log(`ForeignTableSelect(${type}): Current value ${value} not in filtered results, resetting`);
                const event = {
                    target: {
                        name: name,
                        type: 'select-one',
                        selectedOptions: [{ value: '' }]
                    }
                };
                onChange(event);
            }
        }
    }, [type, typeName, value, name, onChange]);

    // データ取得
    useEffect(() => {
        const fetchSelectData = async () => {
            try {
                const response = await execute('GET', protectedResources.getMasterdatas.endpoint + "/" + type);
                if (Array.isArray(response)) {
                    console.log(`ForeignTableSelect: Fetched ${type} data:`, response);
                    setSelectData(response);
                    
                    // 親値に基づいて即時にフィルタリング（データ取得後の初期化）
                    if (relatedInfo && parentValue) {
                        filterDataByParent(response, parentValue, relatedInfo);
                    } else {
                        setFilteredData(response);
                    }
                } else {
                    console.log(`ForeignTableSelect: Response for ${type} is not an array:`, response);
                    setFilteredData([]);
                }
            } catch (error) {
                console.error(`Error fetching ${type} master data:`, error);
                setFilteredData([]);
            }
        }
        fetchSelectData();
    }, [execute, type, filterDataByParent, relatedInfo, parentValue]);

    // 親値が変更されたときのフィルタリング
    useEffect(() => {
        if (selectData.length === 0) return;
        
        console.log(`ForeignTableSelect(${type}): parentValue changed to ${parentValue}`);
        
        if (!relatedInfo || !parentValue) {
            console.log(`ForeignTableSelect(${type}): No relatedInfo or parentValue, showing all options`);
            setFilteredData(selectData);
            return;
        }
        
        filterDataByParent(selectData, parentValue, relatedInfo);
    }, [parentValue, relatedInfo, selectData, type, filterDataByParent]);

    // 表示するデータを決定
    const dataToDisplay = filteredData.length > 0 ? filteredData : selectData;

    // React Selectのオプション形式に変換
    const selectOptions = dataToDisplay.map(data => ({
        value: data.id,
        label: data[typeName]
    }));

    // 現在選択されている値をオブジェクトとして取得
    const selectedOption = selectOptions.find(option => option.value === value);

    // React Selectの変更ハンドラー
    const handleSelectChange = (selectedOption) => {
        const event = {
            target: {
                name: name,
                type: 'select-one',
                selectedOptions: [{ value: selectedOption ? selectedOption.value : '' }]
            }
        };
        onChange(event);
    };

    return (
        <Select
            id={name}
            name={name}
            value={selectedOption || null}
            onChange={handleSelectChange}
            options={selectOptions}
            placeholder="選択してください"
            noOptionsMessage={() => "データがありません"}
            isClearable
            className="basic-select"
            classNamePrefix="select"
        />
    );
}
export default ForeignTableSelect;